import React, { useEffect, useState } from 'react'
import Spinner from '../components/Spinner'
import { changePassword, checkPassword } from '../services/ClownApiClient'
import { ChangePasswordError } from '../services/Errors'

interface InputValues {
  oldPassword: string
  newPassword: string
  confirmPassword: string
}

const ChangePasswordPage = () => {
  const [submitDisabled, setSubmitDisabled] = useState<boolean>(true)
  const [updateErrorMessage, setUpdateErrorMessage] = useState<string>('')
  const [passwordError, setPasswordError] = useState<string>('')
  const [successMessage, setSuccessMessage] = useState<string>('')
  const [passwordMatch, setPasswordMatch] = useState<boolean>(true)
  const [showOldPassword, setShowOldPassword] = useState<boolean>(false)
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false)
  const [inputValues, setInputValues] = useState<InputValues>({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  })
  const [isLoading, setIsLoading] = useState<boolean>(false)

  useEffect(() => {
    if (
      inputValues.oldPassword &&
      inputValues.newPassword &&
      inputValues.confirmPassword &&
      passwordMatch &&
      !passwordError
    ) {
      setSubmitDisabled(false)
    } else {
      setSubmitDisabled(true)
    }
  }, [inputValues, passwordMatch, passwordError])

  const isValidPassword = (str: string) => {
    // Validate that string contains chars between the ascii
    // range of: ' ' (index 32) and '~' (index 126)
    const regexp = new RegExp(/^([\x20-\x7E]+)$/)
    return !!str.match(regexp)
  }

  const isValidPasswordLength = (str: string) => {
    return str.length >= 9
  }

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.currentTarget
    setInputValues({ ...inputValues, [name]: value })

    if (name === 'newPassword') {
      if (!isValidPasswordLength(value)) {
        setPasswordError(
          'The password is too short. It must contain at least 9 characters.',
        )
      } else if (!isValidPassword(value)) {
        setPasswordError('The password contains forbidden characters.')
      } else {
        setPasswordError('')
      }
      if (inputValues.confirmPassword) {
        setPasswordMatch(value === inputValues.confirmPassword)
      }
    }

    if (name === 'confirmPassword') {
      setPasswordMatch(value === inputValues.newPassword)
    }

    setUpdateErrorMessage('')
  }

  const handleSubmit = async () => {
    // Submit the new password to Clown
    setIsLoading(true)
    setSuccessMessage('')
    try {
      await changePassword(
        inputValues.oldPassword,
        inputValues.newPassword,
        inputValues.confirmPassword,
      )
      setUpdateErrorMessage('')
      setSuccessMessage('Password updated successfully')
      setInputValues({ oldPassword: '', newPassword: '', confirmPassword: '' })
    } catch (error) {
      // Inform the user what went wrong
      if (error instanceof ChangePasswordError) {
        setUpdateErrorMessage(error.message)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const validateOnBackend = async (
    event: React.FocusEvent<HTMLInputElement>,
  ) => {
    const { value } = event.currentTarget
    if (isValidPasswordLength(value) && isValidPassword(value)) {
      const response = await checkPassword(event.currentTarget.value)
      if (!response.valid) {
        setPasswordError(response.message)
      } else {
        setPasswordError('')
      }
    }
  }

  return (
    <>
      <h2 className="h4">Change Password</h2>
      <small>
        Passwords must be between 9 and 100 characters and may only contain the
        letters A-Z (in upper or lower case), digits and a restricted range of
        symbols.
      </small>
      <hr />
      {updateErrorMessage && (
        <div className="col-12 alert alert-danger text-center mx-auto">
          <small>{updateErrorMessage}</small>
        </div>
      )}
      {successMessage && (
        <div className="col-12 alert alert-success text-center mx-auto">
          <small>{successMessage}</small>
        </div>
      )}
      <div className="form-group row d-flex align-items-center position-relative">
        <label className="col-12 col-sm-3 muted" htmlFor="oldPassword">
          Old password
        </label>
        <div className="col-12 col-sm-9 position-relative">
          <input
            type={showOldPassword ? 'text' : 'password'}
            className="form-control"
            id="oldPassword"
            name="oldPassword"
            onChange={(e) => handleOnChange(e)}
            value={inputValues.oldPassword}
          />
          <i
            className={`fa ${
              showOldPassword ? 'fa-eye' : 'fa-eye-slash'
            } text-muted position-absolute cursor-pointer`}
            style={{ top: '6px', right: '25px' }}
            onClick={() => setShowOldPassword(!showOldPassword)}
          />
        </div>
      </div>
      <div className="form-group row d-flex align-items-end mt-60 position-relative">
        <label className="col-12 col-sm-3 muted" htmlFor="newPassword">
          New password
        </label>
        <div className="col-12 col-sm-9 position-relative">
          <input
            type={showNewPassword ? 'text' : 'password'}
            className="form-control"
            id="newPassword"
            name="newPassword"
            onChange={(e) => handleOnChange(e)}
            onBlur={(e) => validateOnBackend(e)}
            value={inputValues.newPassword}
            maxLength={100}
          />
          <i
            className={`fa ${
              showNewPassword ? 'fa-eye' : 'fa-eye-slash'
            } text-muted position-absolute cursor-pointer`}
            style={{ top: '6px', right: '25px' }}
            onClick={() => setShowNewPassword(!showNewPassword)}
          />
        </div>
        {passwordError && (
          <div className="ml-auto alert alert-warning mt-5">
            <small>{passwordError}</small>
          </div>
        )}
      </div>
      <div className="form-group row d-flex align-items-end position-relative">
        <label className="col-12 col-sm-3 muted" htmlFor="confirmPassword">
          Confirm password
        </label>
        <div className="col-12 col-sm-9 position-relative">
          <input
            type={showConfirmPassword ? 'text' : 'password'}
            className="form-control"
            id="confirmPassword"
            name="confirmPassword"
            onChange={(e) => handleOnChange(e)}
            value={inputValues.confirmPassword}
          />
          <i
            className={`fa ${
              showConfirmPassword ? 'fa-eye' : 'fa-eye-slash'
            } text-muted position-absolute cursor-pointer`}
            style={{ top: '6px', right: '25px' }}
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
          />
        </div>
        {!passwordMatch && (
          <div className="ml-auto alert alert-warning mt-5">
            <small>Passwords do not match</small>
          </div>
        )}
      </div>
      <div className="d-flex justify-items-end">
        {isLoading ? (
          <div className="ml-auto mt-20">
            <Spinner />
          </div>
        ) : (
          <button
            type="submit"
            className="btn btn-primary ml-auto mt-20"
            disabled={submitDisabled}
            onClick={() => handleSubmit()}>
            Update
          </button>
        )}
      </div>
    </>
  )
}

export default ChangePasswordPage
